const STRUCTURED_FIELDS = ['quantitativeIndicators', 'humanitarianAlerts', 'cdcTravelNotices'] as const;

const isRecord = (value: unknown): value is Record<string, any> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

export function reportCandidateText(candidate: any): string {
    const parts = candidate?.content?.parts;
    return Array.isArray(parts) ? parts.filter(part => !part?.thought && typeof part?.text === 'string')
        .map(part => part.text).join('') : '';
}

export function mergeNestedReportData(outer: any, nested: any): any {
    if (!isRecord(outer) || !isRecord(nested))
        return outer;

    const merged = { ...outer, ...nested };
    for (let index = 0; index < STRUCTURED_FIELDS.length; index++) {
        const field = STRUCTURED_FIELDS[index];
        if (outer[field] !== undefined && outer[field] !== null)
            merged[field] = outer[field];
    }
    return merged;
}

export function mergeFullReportData(report: any): any {
    return mergeNestedReportData(report, report?.fullReportData);
}

export function normalizeReportData(report: any): any {
    let normalized = report;
    const visited = new Set<object>();
    while (isRecord(normalized)) {
        const generic = !normalized.reportType || ['GeminiResponse', 'TextAnalysisResult', 'CompositeReportResult'].includes(normalized.reportType);
        const keys = ['resultData', 'reportData', ...(generic ? ['result', 'response', 'analysis'] : []),
            ...(Array.isArray(normalized.response?.candidates) ? ['response'] : []), 'fullReportData'];
        const nested = keys.map(key => normalized[key]).find(value => isRecord(value) && !visited.has(value));
        if (nested) {
            visited.add(nested);
            normalized = mergeNestedReportData(normalized, nested);
            continue;
        }

        const candidates = normalized.candidates;
        if (!Array.isArray(candidates) || visited.has(candidates))
            break;
        visited.add(candidates);
        const text = reportCandidateText(candidates[0]);
        if (!text.trim()) {
            if ([normalized.summary, normalized.executiveSummary, normalized.content].some(value => typeof value === 'string' && value.trim()) ||
                (Array.isArray(normalized.sections) && normalized.sections.some((section: any) => typeof section.content === 'string' && section.content.trim())))
                break;
            return { ...normalized, reportType: 'Error', errorCode: 'empty_report',
                error: 'No report content was returned when this analysis was created. Your consultant must regenerate it before it can be viewed or exported.' };
        }
        try {
            const parsed = JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
            if (!isRecord(parsed) || !Object.keys(parsed).length)
                throw new Error('Empty report');
            normalized = mergeNestedReportData(normalized, parsed);
        } catch {
            return { ...normalized, reportType: 'Error', error: 'The saved report content could not be read. Please regenerate the analysis or contact your consultant.' };
        }
    }
    return isRecord(normalized) && !normalized.reportType && normalized.type
        ? { ...normalized, reportType: normalized.type } : normalized;
}
